import { API_BASE_URL, normalizeUrl } from "../utils"

export type ReviewMediaKind = "audio" | "video" | "image"

export interface SubmitEventReviewParams {
  eventId: string
  rating: number
  text?: string
  organizationId: string
  sessionId: string
  media?: {
    blob: Blob
    kind: ReviewMediaKind
  }
}

interface EventReviewResponse {
  id: string
  created_at: string
  upload_url?: string | null
}

interface WrappedEventReviewResponse {
  status: number
  code: string
  data: EventReviewResponse
}

const fallbackContentTypes: Record<ReviewMediaKind, string> = {
  audio: "audio/mpeg",
  video: "video/mp4",
  image: "image/jpeg",
}

async function uploadMedia(url: string, blob: Blob, kind: ReviewMediaKind) {
  const fallbackType = fallbackContentTypes[kind]
  const contentType = blob.type && blob.type.trim().length > 0 ? blob.type : fallbackType

  const uploadBlob = blob.type === contentType
    ? blob
    : blob.slice(0, blob.size, contentType)

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: uploadBlob,
  })

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "")
    throw new Error(`Failed to upload media: ${response.status} ${errorBody}`)
  }
}

export async function submitEventReview(params: SubmitEventReviewParams): Promise<EventReviewResponse> {

  const { eventId, rating, text, organizationId, sessionId, media } = params

  const requestBody: Record<string, unknown> = {
    rating,
  }

  requestBody.organization_id = organizationId
  requestBody.session_access_key = sessionId

  if (text && text.trim().length > 0) {
    requestBody.text = text.trim()
  }

  if (media) {
    requestBody.request_upload = {
      type: media.kind,
    }
  }

  const response = await fetch(
    `${normalizeUrl(API_BASE_URL)}/events/${encodeURIComponent(eventId)}/reviews`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    }
  )

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "")
    throw new Error(`Failed to submit review: ${response.status} ${errorBody}`)
  }

  const json = await response.json()

  const payload: EventReviewResponse = (json && typeof json === "object" && "data" in json)
    ? (json as WrappedEventReviewResponse).data
    : (json as EventReviewResponse)

  if (media) {
    const uploadUrl = payload.upload_url

    if (!uploadUrl) {
      throw new Error("Upload URL was not provided for the recording")
    }

    await uploadMedia(uploadUrl, media.blob, media.kind)
  }

  return payload
}
